import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, type ButtonSize } from '@/components/ui/Button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/Popover';
import { useTaskReschedule } from '@/hooks/useTaskReschedule';
import { cn } from '@/lib/utils';
import type { Task } from '@/services/tasks.service';

interface TaskDoneButtonProps {
  taskId: number;
  /** Runs the surface's existing "mark complete" behavior (open a modal or mutate). */
  onComplete: () => void;
  /** Called after a successful reschedule; receives the updated task for optimistic patches. */
  onRescheduled?: (updated: Task) => void;
  /** Show a spinner on the Done button (use for per-row pending flags). */
  completeLoading?: boolean;
  /** Disable both buttons (use for a shared/global pending flag). */
  disabled?: boolean;
  size?: ButtonSize;
  className?: string;
}

/**
 * "Done ▾" split control: the primary button runs `onComplete`; the caret opens a
 * compact menu to reschedule the task (Due +1 Week / +1 Month). Used everywhere a
 * task Done button appears. The Popover content is portaled, so it is never clipped
 * by table-cell overflow, and the wrapper stops row-click propagation so it never
 * opens the row's edit modal.
 */
export function TaskDoneButton({
  taskId,
  onComplete,
  onRescheduled,
  completeLoading = false,
  disabled = false,
  size = 'sm',
  className,
}: TaskDoneButtonProps) {
  const [open, setOpen] = useState(false);
  const { reschedule, rescheduling } = useTaskReschedule();
  const busy = completeLoading || rescheduling !== null;

  async function pick(unit: 'week' | 'month') {
    await reschedule(taskId, unit, onRescheduled);
    setOpen(false);
  }

  return (
    <div className={cn('inline-flex items-center', className)} onClick={(e) => e.stopPropagation()}>
      <Button
        variant="secondary"
        size={size}
        loading={completeLoading}
        disabled={disabled || busy}
        onClick={onComplete}
        className="rounded-r-none"
      >
        Done
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        {/* The icon goes through `leftIcon`, not children: Button drops its
            children entirely when `iconOnly` is set, which rendered this as an
            empty square on every surface that shows a task Done button. */}
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            size={size}
            iconOnly
            leftIcon={<ChevronDown className="h-4 w-4" />}
            disabled={disabled || busy}
            aria-label="Reschedule task"
            className="rounded-l-none border-l-0"
          />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-44 p-1">
          <div className="flex flex-col">
            <button
              type="button"
              className="w-full rounded px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              disabled={rescheduling !== null}
              onClick={() => pick('week')}
            >
              Due +1 Week
            </button>
            <button
              type="button"
              className="w-full rounded px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              disabled={rescheduling !== null}
              onClick={() => pick('month')}
            >
              Due +1 Month
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
